import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Me } from '../types';
import { api, cached, clearSnapshots, readSnapshot, token } from './api';

interface AuthState {
  me: Me | null;
  refresh: () => Promise<void>;
  signIn: (t: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Start from the account saved on the device, so screens know who is signed in without waiting.
  const [me, setMe] = useState<Me | null>(() => (token.get() ? readSnapshot<Me>('/me') : null));
  const refresh = useCallback(async () => {
    try {
      const { data, changed } = await cached<Me>('/me').fresh;
      if (changed) setMe(data);
      else setMe((m) => m ?? data);
    } catch {
      setMe({ user: null, plan: 'free' });
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const signIn = useCallback(
    async (t: string) => {
      clearSnapshots();
      token.set(t);
      await refresh();
    },
    [refresh],
  );
  const signOut = useCallback(async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
    token.set(null);
    clearSnapshots();
    await refresh();
  }, [refresh]);
  return <AuthContext.Provider value={{ me, refresh, signIn, signOut }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('AuthProvider missing');
  return ctx;
}
